// shell/components/heat-map/heat-map-shader.ts

/**
 * Bounds for heat map calculation.
 */
export interface HeatMapBounds {
    min: number;
    max: number;
    avg: number;
}

/**
 * Configuration for heat map colors.
 */
export interface HeatMapConfig {
    /** Color for minimum values (default: #181818) */
    minColor?: string;
    /** Color for average values (default: #424242) */
    midColor?: string;
    /** Color for maximum values (default: #666666) */
    maxColor?: string;
    /** Opacity for all colors (default: 0.6) */
    opacity?: number;
}

interface RGB {
    r: number;
    g: number;
    b: number;
}

function parseHex(hex: string): RGB {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
}

/**
 * HeatMapShader calculates and applies heat-map coloring to elements.
 *
 * Uses a three-point gradient: min -> avg -> max
 * Values below avg interpolate between minColor and midColor.
 * Values above avg interpolate between midColor and maxColor.
 */
export class HeatMapShader {
    private readonly minColor: RGB;
    private readonly midColor: RGB;
    private readonly maxColor: RGB;
    private readonly opacity: number;

    constructor(config?: HeatMapConfig) {
        this.minColor = parseHex(config?.minColor ?? '#181818');
        this.midColor = parseHex(config?.midColor ?? '#424242');
        this.maxColor = parseHex(config?.maxColor ?? '#666666');
        this.opacity = config?.opacity ?? 0.6;
    }

    /**
     * Compute bounds from an array of values.
     * Returns null if array is empty.
     */
    computeBounds(values: number[]): HeatMapBounds | null {
        return HeatMapShader.computeBoundsFromArray(values);
    }

    /**
     * Static version for computing bounds without instance.
     */
    static computeBoundsFromArray(values: number[]): HeatMapBounds | null {
        if (values.length === 0) return null;

        let min = values[0];
        let max = values[0];
        let sum = 0;

        for (const v of values) {
            if (v < min) min = v;
            if (v > max) max = v;
            sum += v;
        }

        return {
            min,
            max,
            avg: sum / values.length
        };
    }

    /**
     * Get color for a value within bounds.
     * Returns null if bounds are invalid (min === max).
     */
    getColor(value: number, bounds: HeatMapBounds): string | null {
        if (!bounds || bounds.min === bounds.max) return null;

        // Clamp to bounds
        if (value <= bounds.min) return this.rgbaString(this.minColor);
        if (value >= bounds.max) return this.rgbaString(this.maxColor);
        if (value === bounds.avg) return this.rgbaString(this.midColor);

        // Determine which half of the gradient
        let lowColor: RGB;
        let highColor: RGB;
        let min: number;
        let max: number;

        if (value < bounds.avg) {
            lowColor = this.minColor;
            highColor = this.midColor;
            min = bounds.min;
            max = bounds.avg;
        } else {
            lowColor = this.midColor;
            highColor = this.maxColor;
            min = bounds.avg;
            max = bounds.max;
        }

        // Interpolate
        const factor = (value - min) / (max - min);
        const r = Math.round(lowColor.r + (highColor.r - lowColor.r) * factor);
        const g = Math.round(lowColor.g + (highColor.g - lowColor.g) * factor);
        const b = Math.round(lowColor.b + (highColor.b - lowColor.b) * factor);

        return `rgba(${r}, ${g}, ${b}, ${this.opacity})`;
    }

    /**
     * Apply color to an element's background.
     */
    applyToElement(element: HTMLElement, value: number, bounds: HeatMapBounds): void {
        const color = this.getColor(value, bounds);
        element.style.backgroundColor = color ?? '';
    }

    /**
     * Clear heat map styling from an element.
     */
    clearFromElement(element: HTMLElement): void {
        element.style.backgroundColor = '';
    }

    private rgbaString(rgb: RGB): string {
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${this.opacity})`;
    }

    // === Static ranking methods ===

    /**
     * Rank a number for heat map (identity function, handles NaN).
     */
    static rankNumber(value: number): number {
        return isNaN(value) ? 0 : value;
    }

    /**
     * Rank a Date for heat map (converts to minutes since epoch).
     */
    static rankDate(value: Date | string): number {
        if (!value) return 0;
        const date = typeof value === 'string' ? new Date(value) : value;
        const minutes = Math.floor(date.getTime() / 1000 / 60);
        return isNaN(minutes) ? 0 : minutes;
    }

    /**
     * Rank a string for heat map (character-based ranking).
     * Useful for alphabetical sorting visualization.
     *
     * @param value - String to rank
     * @param maxLength - Maximum length to consider (default: 20)
     */
    static rankString(value: string | undefined, maxLength = 20): number {
        if (!value || value.length === 0) return 0;

        // Pad or truncate to maxLength
        const str = value.length > maxLength
            ? value.substring(0, maxLength)
            : value.padEnd(maxLength, '0');

        let result = 0;
        const base = 38; // a-z (26) + 0-9 (10) + special (2)

        for (let i = 0; i < maxLength; i++) {
            const c = str.charAt(i);
            let n: number;

            if (c === '-') n = 1;
            else if (c === '_') n = 0;
            else if (c >= 'a' && c <= 'z') n = c.charCodeAt(0) - 97 + 2;
            else if (c >= 'A' && c <= 'Z') n = c.charCodeAt(0) - 65 + 2;
            else if (c >= '0' && c <= '9') n = parseInt(c) + 28;
            else n = 0;

            result += Math.pow(base, maxLength - i - 1) * n;
        }

        return result;
    }

    /**
     * Rank an IP address for heat map.
     * Converts IPv4 address to a 32-bit integer.
     */
    static rankIpAddress(value: string): number {
        const parts = value.split('.');
        if (parts.length !== 4) return 0;

        const a = parseInt(parts[0]);
        const b = parseInt(parts[1]);
        const c = parseInt(parts[2]);
        const d = parseInt(parts[3]);

        if (isNaN(a) || isNaN(b) || isNaN(c) || isNaN(d)) return 0;

        return (
            a * 16777216 + // 256^3
            b * 65536 +    // 256^2
            c * 256 +
            d
        );
    }
}
